import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { ProviderAccessLog } from '../types';
import { RequestStatus } from 'types/RequestStatus';

export interface UserLevelFormState {
	providerCode: string;
	startDate: Date;
	endDate: Date;
	realtimeOnly: boolean;
	applicationId: string;
}

export type UserLevelFormContextType = {
	formState: UserLevelFormState;
	setFormState: React.Dispatch<React.SetStateAction<UserLevelFormState>>;
	providerAccessLogs: ProviderAccessLog[];
	setProviderAccessLogs: React.Dispatch<
		React.SetStateAction<ProviderAccessLog[]>
	>;
	accessEntriesStatus: RequestStatus;
	setAccessEntriesStatus: React.Dispatch<React.SetStateAction<RequestStatus>>;
};

const defaultFormState: UserLevelFormState = {
	providerCode: '',
	startDate: new Date(),
	endDate: new Date(),
	realtimeOnly: false,
	applicationId: '',
};

export const UserLevelFormContext =
	createContext<UserLevelFormContextType | null>(null);

const UserLevelFormContextProvider: FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [formState, setFormState] =
		useState<UserLevelFormState>(defaultFormState);
	const [providerAccessLogs, setProviderAccessLogs] = useState<
		ProviderAccessLog[]
	>([]);
	const [accessEntriesStatus, setAccessEntriesStatus] =
		useState<RequestStatus>(RequestStatus.UNLOADED);

	return (
		<UserLevelFormContext.Provider
			value={{
				formState,
				setFormState,
				providerAccessLogs,
				setProviderAccessLogs,
				accessEntriesStatus,
				setAccessEntriesStatus,
			}}
		>
			{children}
		</UserLevelFormContext.Provider>
	);
};

export function useUserLevelFormContext() {
	const context = useContext(UserLevelFormContext);
	if (!context) {
		throw new Error(
			'useUserLevelFormContext must be used within UserLevelFormContextProvider',
		);
	}
	return context;
}

export default UserLevelFormContextProvider;
